import {MissionsApi, Token} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {useQuery} from "@tanstack/react-query";

// TODO: replace these interfaces with types from @devour/client
interface ObjectiveProgressResponse {
    objectiveId: string;
    isComplete: boolean;
    amount: number;
    sessionAmount?: number;
    gameId?: string;
    completedCheckpoints: number[];
    claimedCheckpoints: number[];
    claimedAt?: number;
}

export interface PlayableMissionCreative {
    displayName?: string;
    displayColor?: string;
    logoAssetId?: string;
    logo1x1AssetId?: string;
    bannerAssetId?: string;
    backgroundImageAssetId?: string;
    externalUrl?: string;
    ctaText?: string;
    rewardImageAssetId?: string;
    revealText?: string;
    ctaButtonText?: string;
    brandLinkText?: string;
    questHeader?: string;
    ctaStripColor?: string;
    playableAdLayout?: string;
    headlineFont?: string;
    completeHeadline?: string;
    completeSubtext?: string;
    completeButtonText?: string;
    claimedHeadline?: string;
    claimedSubtext?: string;
    claimedSponsorMessage?: string;
    claimedCtaText?: string;
    claimedCtaUrl?: string;
    cappedHeadline?: string;
    cappedSubtext?: string;
    cappedSponsorMessage?: string;
}

export interface PlayableMissionObjective {
    objectiveTemplateId: string;
    name: string;
    description?: string;
    category?: string;
    logicType: string;
    targetAmount: number;
    sessionAmount?: number;
    sources: string[];
    subtype?: string;
    gameId?: string;
    rewardConfigIds?: string[];
    rewardPer?: number;
    displayDivider?: number;
    displayPriority?: number;
}

export interface PlayableMissionProgression {
    id: string;
    userId: string;
    activationId: string;
    progression: Record<string, ObjectiveProgressResponse>;
    prerequisites: Record<string, boolean>;
    isComplete: boolean;
    claimedAt?: number;
    cappedMessageSeenAt?: number;
    lastStatReset: number;
    pointsReset: number;
    pointsTotal: number;
    completionCount: number;
    enrolledAt?: number;
    segmentIndex?: number;
    createdAt: number;
    updatedAt: number;
}

export interface PlayableMissionAvailability {
    maxCompletions?: number;
    currentCompletions: number;
    maxClaims?: number;
    currentClaims: number;
    isCapped: boolean;
}

export interface PlayableMissionResponse {
    id: string;
    name: string;
    creative?: PlayableMissionCreative;
    objectives: PlayableMissionObjective[];
    startAt?: number;
    endAt?: number;
    playableAdConfig?: {
        adFormat: string;
        placement: string;
        maxCompletionsPerUser?: number;
        maxCompletions?: number;
        maxClaims?: number;
    };
    bonusRewardConfigIds?: string[];
    rewardAutoSettings?: Record<string, boolean>;
    gameIconAssetId?: string;
    progression?: PlayableMissionProgression;
    availability?: PlayableMissionAvailability;
}

export interface UseGetPlayableMissionsParams {
    utmSource: string | null;
    externalGameId?: string;
}

export const USE_GET_PLAYABLE_MISSIONS_KEY = "playableMissions";

async function fetchPlayableMissions(
    params: UseGetPlayableMissionsParams,
    fullToken?: Token,
): Promise<PlayableMissionResponse[]> {
    const api = new MissionsApi(getConfig(fullToken));
    return api.getMissionsPlayable({
        utmSource: params.utmSource!,
        externalGameId: params.externalGameId,
    }) as Promise<PlayableMissionResponse[]>;
}

export function useGetPlayableMissions(
    params: UseGetPlayableMissionsParams,
    fullToken?: Token,
) {
    return useQuery({
        queryKey: [USE_GET_PLAYABLE_MISSIONS_KEY, params.utmSource, params.externalGameId, fullToken?.token],
        queryFn: () => fetchPlayableMissions(params, fullToken),
        enabled: !!params.utmSource,
        staleTime: 30000,
    });
}
